import { apiClient, apiRequest } from '@/shared/api/client';
import { env } from '@/shared/config/env';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { FaixaFaturamento, OnboardingPayload, TipoPerfil, User } from '../model/types';

const apiHost = (() => {
  const url = new URL(env.VITE_API_URL);
  return `${url.protocol}//${url.host}`;
})();

/**
 * Resolve a URL absoluta do avatar e, quando `version` esta presente,
 * anexa `?v=<version>` como cache-buster. Backend serve a foto em URL
 * estavel (ex: /uploads/avatars/{userId}.jpg); sem o querystring, o
 * browser reusa a versao em cache apos um upload. Apenas o consumer que
 * sabe sobre uma atualizacao local (fotoUpdatedAt do authStore) deve
 * passar `version` -- a hidratacao normal mantem URL limpa para usar
 * cache HTTP corretamente.
 */
export const absoluteAvatarUrl = (
  url: string | null | undefined,
  version?: number | null,
): string | null => {
  if (!url) return null;
  const base =
    url.startsWith('http://') || url.startsWith('https://') ? url : `${apiHost}${url}`;
  if (version == null) return base;
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}v=${version}`;
};

export type UpdateProfilePayload = Partial<{
  nome: string;
  whatsapp: string;
  cargo: string;
  empresa: string;
  segmento: string;
  cidade: string;
  estado: string;
  tipoPerfil: TipoPerfil;
  faturamento: FaixaFaturamento;
  tamanhoTime: number;
  gargalo: string;
  objetivoNetworking: string;
  consentSponsor: boolean;
}>;

type ApiUserProfileDto = {
  id: string;
  email: string;
  nome: string | null;
  avatarUrl: string | null;
  tipoPerfil: string | null;
  cargo: string | null;
  empresa: string | null;
  segmento: string | null;
  faturamento: string | null;
  tamanhoTime: string | null;
  cidade: string | null;
  estado: string | null;
  whatsapp: string | null;
  gargalo: string | null;
  presencialOnline: string | null;
  networkingLevel: number;
  networkingColor: string;
  frequenciaEventos: string | null;
  objetivoNetworking: string | null;
  mentorMode: boolean;
  onboardingCompleto: boolean;
  plano: 'FREE' | 'PREMIUM';
  qrActivated: boolean;
  consentSponsor: boolean;
  tags: { busca: string[]; oferta: string[] };
};

const tagsToObjects = (tags: string[]): { id: string; nome: string }[] =>
  tags.map((nome) => ({ id: nome, nome }));

const parseInteger = (value: string | null): number | null => {
  if (!value) return null;
  const n = Number.parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
};

export const fromUserDto = (dto: ApiUserProfileDto): User => ({
  id: dto.id,
  email: dto.email,
  nome: dto.nome ?? '',
  fotoUrl: dto.avatarUrl,
  cargo: dto.cargo,
  empresa: dto.empresa,
  segmento: dto.segmento,
  estado: dto.estado,
  tipoPerfil: (dto.tipoPerfil as TipoPerfil | null) ?? null,
  faturamento: (dto.faturamento as FaixaFaturamento | null) ?? null,
  tamanhoTime: parseInteger(dto.tamanhoTime),
  whatsapp: dto.whatsapp,
  cidade: dto.cidade,
  gargalo: dto.gargalo,
  consentSponsor: dto.consentSponsor,
  onboardingCompleto: dto.onboardingCompleto,
  plano: dto.plano,
  bio: null,
  tagsBusca: tagsToObjects(dto.tags?.busca ?? []),
  tagsOferta: tagsToObjects(dto.tags?.oferta ?? []),
});

export const userApi = {
  getMe: async (): Promise<{ user: User }> => {
    const dto = await apiRequest<ApiUserProfileDto>({ method: 'GET', url: ENDPOINTS.USERS.ME });
    return { user: fromUserDto(dto) };
  },
  updateMe: async (data: UpdateProfilePayload): Promise<{ user: User }> => {
    const payload: Record<string, unknown> = { ...data };
    if (data.tamanhoTime != null) {
      payload.tamanhoTime = String(data.tamanhoTime);
    }
    const dto = await apiRequest<ApiUserProfileDto>({
      method: 'PATCH',
      url: ENDPOINTS.USERS.ME,
      data: payload,
    });
    return { user: fromUserDto(dto) };
  },
  submitOnboarding: async (payload: OnboardingPayload): Promise<{ user: User }> => {
    const dto = await apiRequest<ApiUserProfileDto>({
      method: 'POST',
      url: ENDPOINTS.USERS.ONBOARDING,
      data: payload,
    });
    return { user: fromUserDto(dto) };
  },
  uploadAvatar: async (file: File): Promise<{ user: User }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.request<{
      success: boolean;
      data: ApiUserProfileDto;
      error?: { code: string; message: string };
    }>({
      method: 'POST',
      url: `${ENDPOINTS.USERS.ME}/avatar`,
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'Falha no upload');
    }
    return { user: fromUserDto(response.data.data) };
  },
};
